import {
  DataGridPro as MuiDataGridPro,
  gridClasses,
  type DataGridProProps,
  type GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { alpha } from '@mui/material/styles';
import { LoadingTable } from '@/primitives';
import {
  FOOTER_HEIGHT,
  HEADER_CONFIGURATION_HEIGHT,
  HEADER_HEIGHT,
  NAV_HEIGHT,
  PAGE_PADDING_TOP,
  TABLE_BOTTOM_PADDING,
} from '@/configs/layout';
import { formatPaginationDisplayedRows } from '@/lib/formatPaginationDisplayedRows';

export {
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
  type GridCellParams,
  type GridColDef,
  type GridPaginationModel,
  type GridRowParams,
  type GridSortModel,
  type GridFilterModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';

export type { GridToolbarContainerProps } from '@mui/x-data-grid-pro';

export function DataGridPro<T extends GridValidRowModel>(
  props: DataGridProProps<T> & {
    isConfiguration?: boolean;
    isFetching?: boolean;
    minusExtraHeight?: number;
    /** Fill height from flex parent (registry pages); omit viewport calc. */
    fillAvailableHeight?: boolean;
  },
) {
  const {
    isConfiguration,
    isFetching,
    minusExtraHeight = 0,
    fillAvailableHeight = false,
    sx: sxProp,
    slotProps: slotPropsProp,
    ...rest
  } = props;

  if (isFetching) {
    return <LoadingTable />;
  }

  const slotProps = {
    ...slotPropsProp,
    pagination: {
      ...slotPropsProp?.pagination,
      labelDisplayedRows:
        (
          slotPropsProp?.pagination as
            | { labelDisplayedRows?: typeof formatPaginationDisplayedRows }
            | undefined
        )?.labelDisplayedRows ?? formatPaginationDisplayedRows,
    },
  } as DataGridProProps<T>['slotProps'];

  const localeText = {
    ...rest.localeText,
    // Support custom uppercase operator values used in filters
    filterOperatorCONTAINS: 'Contains',
    filterOperatorENDS_WITH: 'Ends With',
    filterOperatorEQUALS: 'Equals',
    filterOperatorGREATER_THAN: 'Greater Than',
    filterOperatorNOT_NULL: 'Is Not Empty',
    filterOperatorLESS_THAN: 'Less Than',
    filterOperatorNOT_EQUALS: 'Not Equals',
    filterOperatorSTARTS_WITH: 'Starts With',
  } as const;

  return (
    <MuiDataGridPro
      columnHeaderHeight={48}
      rowHeight={44}
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
      }
      localeText={localeText}
      slotProps={slotProps}
      {...rest}
      sx={[
        (theme) => ({
          ...(fillAvailableHeight
            ? {
                flex: 1,
                minHeight: 0,
                height: '100%',
                width: '100%',
              }
            : {
                height: `calc(100vh - ${NAV_HEIGHT}px - ${PAGE_PADDING_TOP}px - ${
                  isConfiguration ? HEADER_CONFIGURATION_HEIGHT : HEADER_HEIGHT
                }px - ${TABLE_BOTTOM_PADDING}px - ${FOOTER_HEIGHT}px - ${minusExtraHeight}px)`,
              }),
          [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
            outline: 'transparent',
          },
          [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
            {
              outline: 'none',
            },
          '& .MuiDataGrid-selectedRowCount': {
            visibility: 'hidden',
          },
          '&.MuiDataGrid-root': {
            borderWidth: '0px',
            minHeight: '200px',
          },
          '.MuiDataGrid-topContainer::after': {
            backgroundColor: 'unset',
          },
          '.MuiDataGrid-toolbar': {
            borderBottom: 'none',
          },
          // Match column headers (background.paper); MUI defaults use vars.header.background.base
          // which diverges in dark mode. v8: no scrollbarFiller--header class — use gridClasses.
          [`& .${gridClasses.columnHeaders} .${gridClasses.scrollbarFiller}`]: {
            borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`,
            bgcolor: 'background.paper',
          },
          [`& .${gridClasses.columnHeaders} .${gridClasses.filler}`]: {
            bgcolor: 'background.paper',
          },
          '.MuiDataGrid-columnHeader': {
            color: 'text.primary',
            bgcolor: 'background.paper',
            paddingTop: '4px',
            paddingBottom: '4px',
            paddingLeft: '10px',
            paddingRight: '10px',
            fontWeight: 400,
          },
          '.MuiDataGrid-columnHeaderTitleContainerContent': {
            fontSize: '12px',
            fontWeight: 400,
            whiteSpace: 'normal',
            lineHeight: 1.3,
            color: 'text.primary',
          },
          '.MuiDataGrid-columnHeaderTitle': {
            fontSize: '12px',
            fontWeight: 400,
            whiteSpace: 'normal',
            lineHeight: 1.3,
          },
          '.MuiDataGrid-columnHeader .MuiTypography-root': {
            fontWeight: 400,
            fontSize: '12px',
          },
          '.MuiDataGrid-columnHeader.pl': {
            paddingLeft: '14px',
          },
          // Align header “select all” checkbox with row checkboxes (.pl would add extra left padding)
          [`& .${gridClasses.columnHeader}.${gridClasses.columnHeaderCheckbox}`]:
            {
              paddingLeft: '4px',
              paddingRight: '8px',
            },
          '.MuiDataGrid-columnHeader--pinnedRight > .MuiDataGrid-columnSeparator':
            {
              display: 'none',
            },
          '.MuiDataGrid-columnHeader--withLeftBorder': {
            borderLeft: 'none',
          },
          '.MuiDataGrid-filler--pinnedRight': {
            borderLeft: 'none',
          },
          '.MuiDataGrid-row': {
            height: '44px',
          },
          '.MuiDataGrid-row.even .MuiDataGrid-cell--pinnedRight': {
            bgcolor: 'background.default',
          },
          '.MuiDataGrid-row.odd .MuiDataGrid-cell--pinnedRight': {
            bgcolor: 'background.paper',
          },
          '.MuiDataGrid-cell': {
            borderWidth: '0px',
            borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`,
            fontSize: '13px',
            fontWeight: '400',
            letterSpacing: '0.25px',
            alignContent: 'center',
            margin: 0,
            paddingTop: '0px',
            paddingBottom: '0px',
            paddingLeft: '8px',
            paddingRight: '8px',
            color: 'text.primary',
          },
          [`& .${gridClasses.cell} .MuiChip-root`]: {
            height: '22px',
            minHeight: '22px',
            '& .MuiChip-label': {
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: '8px',
              paddingRight: '8px',
              fontSize: '12px',
              lineHeight: 1.25,
            },
          },
          '& .MuiDataGrid-main': {
            boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
            overflow: 'hidden',
          },
          '.MuiDataGrid-withBorderColor': {
            borderColor: (theme.vars || theme).palette.divider,
          },
          '.MuiDataGrid-footerContainer': {
            marginTop: '12px',
            paddingTop: '7px',
            paddingBottom: '4px',
            paddingRight: '16px',
            borderWidth: '0px',
            minHeight: '46px',
            bgcolor: 'background.default',
            color: 'text.secondary',
          },
          '.even': {
            bgcolor: 'background.default',
          },
          '.odd': {
            bgcolor: 'background.paper',
          },
          [`& .${gridClasses.row}:hover`]: {
            backgroundColor: theme.palette.action.hover,
          },
          '.MuiTablePagination-displayedRows': {
            fontSize: '12px',
            color: '#64748B',
            minWidth: 'auto',
          },
          '.MuiTablePagination-selectLabel': {
            fontSize: '12px',
            color: '#64748B',
          },
          '.MuiTablePagination-root': {
            color: '#64748B',
            minHeight: '36px',
            '.MuiTablePagination-toolbar': {
              minHeight: '36px',
              paddingLeft: '8px',
              paddingRight: '8px',
            },
            '.MuiTablePagination-input': {
              border: '1px solid #2563EB',
              borderRadius: '4px',
              color: '#2563EB',
              minHeight: '28px',
              fontSize: '12px',
              margin: 0,
              marginLeft: '8px',
              marginRight: '24px',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            },
            '.MuiTablePagination-select': {
              paddingBlock: '4px',
              paddingInline: '8px',
              paddingRight: '24px !important',
              fontSize: '12px',
            },
            '.MuiTablePagination-input svg': {
              color: '#2563EB',
              fontSize: '18px',
              right: '4px',
            },
            '.MuiTablePagination-actions .MuiButtonBase-root': {
              borderRadius: '4px',
              width: '28px',
              height: '28px',
              padding: '4px',
              mr: 0.25,
              svg: {
                fontSize: '16px',
              },
              '&:not(.Mui-disabled)': {
                color: '#2563EB',
                backgroundColor: '#EFF6FF',
              },
            },
          },
        }),
        ...(Array.isArray(sxProp) ? sxProp : sxProp ? [sxProp] : []),
      ]}
    />
  );
}
